import type { CSSProperties, ReactNode } from 'react';
import { useId } from 'react';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';

import { wacToken } from '@/libraries/design/foundations';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

/**
 * Option shape for the ResultsCard's optional dropdown.
 */
export type ResultsCardDropdownOption = {
	value: string;
	label: string;
};

/**
 * Props for the ResultsCard dropdown control. When provided, a labeled
 * native `<select>` is rendered above the value-add footer (or as the
 * last content section, if no footer).
 *
 * The dropdown is a controlled component — the consumer owns state and
 * receives the newly-selected value via `onChange`.
 */
export type ResultsCardDropdownProps = {
	/**
	 * Screen-reader label for the dropdown (also used as the ID prefix for
	 * the underlying `<select>` element). Not visually rendered.
	 */
	ariaLabel: string;
	/**
	 * Placeholder text shown when `value` is empty. Matches Figma's
	 * `[Usage level]` placeholder style.
	 */
	placeholder?: string;
	/** Selectable options. */
	options: ResultsCardDropdownOption[];
	/** Currently-selected value (controlled). */
	value: string;
	/** Called with the newly-selected option value on user change. */
	onChange: (value: string) => void;
};

/**
 * ResultsCard — presentational card for the Estimate step's summary panels.
 *
 * Per Figma node 642:6143 (parent) with variations at 642:6146 (base),
 * 642:6172 (base + coverage marketing), and 642:6119 (base + dropdown +
 * value-add footer).
 *
 * All content sections are opt-in via props — the card gracefully handles
 * any combination:
 *   - eyebrow + headline (minimum)
 *   - + headlineSubtext
 *   - + coverageText + optional bullets (bold callout + list)
 *   - + dropdown (usage-level selector)
 *   - + footerNote (blue-background dynamic copy)
 *
 * This is a pure presentational component: no analytics, no context,
 * no state. Callers own all state (dropdown value, footer text derivation).
 */
export type ResultsCardProps = {
	/** Uppercase eyebrow / label above the headline (14/18). */
	eyebrow: string;
	/** Big H3-sized headline (32/38 Charlie Display Bold). */
	headline: string;
	/** Optional subtext directly under the headline (16/24 Charlie Text). */
	headlineSubtext?: string;
	/**
	 * Optional bold body callout under the headline block.
	 * Renders 16/24 Charlie Text Bold. May be a string or a `ReactNode`
	 * for richer formatting (e.g. inline `<strong>` spans).
	 */
	coverageText?: ReactNode;
	/**
	 * Optional bulleted list rendered under the coverage text.
	 * Only rendered when non-empty. Each entry becomes a `<li>`.
	 */
	bullets?: string[];
	/** Optional dropdown — see `ResultsCardDropdownProps`. */
	dropdown?: ResultsCardDropdownProps;
	/**
	 * Optional blue-background footer note. Dynamic copy that changes based
	 * on the consumer's state (e.g. the selected dropdown option).
	 */
	footerNote?: ReactNode;
	/**
	 * Optional testId forwarded to the outer container for E2E targeting.
	 * Defaults to `"results-card"`.
	 */
	testId?: string;
};

export const ResultsCard = ({
	eyebrow,
	headline,
	headlineSubtext,
	coverageText,
	bullets,
	dropdown,
	footerNote,
	testId = 'results-card',
}: ResultsCardProps) => {
	// Stable ID for the native select's aria-label / label-for pairing.
	const uid = useId();
	const selectId = dropdown ? `${testId}-${uid}-select` : undefined;

	// Locally-typed bullets array — avoids a non-null assertion in the
	// JSX below and gives TypeScript a narrowed `string[]` inside the guard.
	const safeBullets: string[] = Array.isArray(bullets) && bullets.length > 0 ? bullets : [];
	const hasBullets = safeBullets.length > 0;

	return (
		<TypographyStyles>
			<div style={cardStyle} data-testid={testId}>
				<div style={cardInnerStyle}>
					<div style={contentStyle}>
						<div style={eyebrowStyle}>{eyebrow}</div>

						<div style={recommendationStyle}>
							<p style={headlineStyle}>{headline}</p>
							{headlineSubtext ? <p style={headlineSubtextStyle}>{headlineSubtext}</p> : null}
						</div>

						{coverageText || hasBullets ? (
							<div style={coverageStyle}>
								{coverageText ? <p style={coverageTextStyle}>{coverageText}</p> : null}
								{hasBullets ? (
									<ul style={bulletListStyle}>
										{safeBullets.map((bullet, index) => (
											<li key={`${bullet}-${index}`} style={bulletItemStyle}>
												{bullet}
											</li>
										))}
									</ul>
								) : null}
							</div>
						) : null}

						{dropdown ? (
							<div style={dropdownWrapperStyle}>
								{/* SR-only label paired to the native <select>. */}
								<label htmlFor={selectId} style={visuallyHiddenStyle}>
									{dropdown.ariaLabel}
								</label>
								<div style={dropdownPillStyle}>
									<select
										id={selectId}
										value={dropdown.value}
										onChange={(e) => dropdown.onChange(e.target.value)}
										style={selectStyle}
										data-testid={`${testId}-select`}
									>
										{dropdown.placeholder ? (
											<option value="" disabled>
												{dropdown.placeholder}
											</option>
										) : null}
										{dropdown.options.map((opt) => (
											<option key={opt.value} value={opt.value}>
												{opt.label}
											</option>
										))}
									</select>
									<span style={chevronStyle} aria-hidden="true">
										<ChevronDownIcon label="" />
									</span>
								</div>
							</div>
						) : null}
					</div>
				</div>

				{footerNote ? (
					<div style={footerStyle} data-testid={`${testId}-footer`}>
						<p style={footerTextStyle}>{footerNote}</p>
					</div>
				) : null}
			</div>
		</TypographyStyles>
	);
};

// ─────────────────────────────────────────────────────────────────────────
// Styles — matching Figma tokens from node 642:6143.
// The card lives in the calculator's estimate/summary steps; keep all
// styling colocated so future designers can eyeball it in one place.
// ─────────────────────────────────────────────────────────────────────────

const cardStyle: CSSProperties = {
	// Figma: neutral/opaque/neutral100 (#F8F8F8), 20px rounded corners,
	// no border, overflow hidden (so the blue footer clips its top corners
	// against the rounded card).
	backgroundColor: wacToken('color.neutral.light100'),
	borderRadius: '20px',
	overflow: 'hidden',
	// Column flex so the value-add footer sits flush against the bottom.
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	width: '100%',
};

const cardInnerStyle: CSSProperties = {
	// Figma padding: 20px sides, 20px top, 32px bottom.
	padding: '20px 20px 32px 20px',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	width: '100%',
	boxSizing: 'border-box',
};

const contentStyle: CSSProperties = {
	// Figma gap: space/300 (24px) between eyebrow / recommendation /
	// coverage / dropdown sections.
	display: 'flex',
	flexDirection: 'column',
	gap: '24px',
	alignItems: 'stretch',
};

const eyebrowStyle: CSSProperties = {
	// Figma "More Styles / Eyebrow - Regular": 14/18 Charlie Text Regular,
	// uppercase, Primary Black at 80% opacity.
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '14px',
	lineHeight: '18px',
	fontWeight: 400,
	letterSpacing: 0,
	textTransform: 'uppercase',
	// Figma spec uses `rgba(0,0,0,0.8)`. WAC's design system has no
	// opacity-adjusted token that matches exactly; using the solid Primary
	// Black (`color.neutral.light1100` = #101214) provides equivalent
	// contrast and stays token-compliant.
	color: wacToken('color.neutral.light1100'),
};

const recommendationStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: '0px',
	alignItems: 'stretch',
};

const headlineStyle: CSSProperties = {
	// Figma "Desktop / D - H3": 32/38 Charlie Display Bold, tracking 0.3px.
	margin: 0,
	fontFamily: wacToken('font.charlie.display'),
	fontSize: '32px',
	lineHeight: '38px',
	fontWeight: 700,
	letterSpacing: '0.3px',
	color: wacToken('color.neutral.light1100'),
};

const headlineSubtextStyle: CSSProperties = {
	// Figma "Desktop / D - Body Medium": 16/24 Charlie Text Regular.
	margin: 0,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '24px',
	fontWeight: 400,
	color: wacToken('color.neutral.light1100'),
};

const coverageStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: '8px',
	alignItems: 'stretch',
};

const coverageTextStyle: CSSProperties = {
	// Figma "Desktop / D - Body Medium - Bold": 16/24 Charlie Text Bold.
	margin: 0,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '24px',
	fontWeight: 700,
	color: wacToken('color.neutral.light1100'),
};

const bulletListStyle: CSSProperties = {
	margin: 0,
	// 21px left indent from Figma matches the ContentBlock bullet spec.
	paddingInlineStart: '21px',
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '14px',
	lineHeight: '20px',
	fontWeight: 400,
	color: wacToken('color.neutral.light1100'),
	listStyle: 'disc',
};

const bulletItemStyle: CSSProperties = {
	// Figma: 8px between bullets. Last-child handled by natural absence of margin.
	marginBottom: '8px',
};

const dropdownWrapperStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
};

const dropdownPillStyle: CSSProperties = {
	// Figma "Selector": white bg, 1px #7D818A border, 20px pill, 8/16 padding.
	// Min-width 202px, max-width 368px per Figma constraints.
	position: 'relative',
	display: 'inline-flex',
	alignItems: 'center',
	backgroundColor: wacToken('color.neutral.light0'),
	border: `1px solid ${wacToken('color.neutral.light600')}`,
	borderRadius: '20px',
	minWidth: '202px',
	maxWidth: '368px',
	width: '100%',
	boxSizing: 'border-box',
};

const selectStyle: CSSProperties = {
	// Reset native <select> styling; the wrapping pill provides the visual
	// affordance. `appearance: none` removes the browser chevron so our
	// decorative one is the only chevron visible.
	appearance: 'none',
	WebkitAppearance: 'none',
	MozAppearance: 'none',
	flex: 1,
	padding: '8px 40px 8px 16px', // right padding leaves room for chevron
	border: 'none',
	outline: 'none',
	background: 'transparent',
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '24px',
	color: wacToken('color.neutral.light1100'),
	cursor: 'pointer',
};

const chevronStyle: CSSProperties = {
	position: 'absolute',
	right: '16px',
	top: '50%',
	transform: 'translateY(-50%)',
	display: 'inline-flex',
	alignItems: 'center',
	pointerEvents: 'none',
	color: wacToken('color.neutral.light1100'),
};

const footerStyle: CSSProperties = {
	// Figma "value add": Extended/Blues/B100 (#E9F2FE) bg, 12px padding.
	backgroundColor: wacToken('color.extended.blue100'),
	padding: '12px',
	display: 'flex',
	alignItems: 'flex-start',
};

const footerTextStyle: CSSProperties = {
	margin: 0,
	flex: 1,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '24px',
	fontWeight: 400,
	color: wacToken('color.neutral.light1100'),
};

const visuallyHiddenStyle: CSSProperties = {
	position: 'absolute',
	width: '1px',
	height: '1px',
	padding: 0,
	margin: '-1px',
	overflow: 'hidden',
	clip: 'rect(0, 0, 0, 0)',
	whiteSpace: 'nowrap',
	border: 0,
};
